/**
 * Paddle.js loader + env config — lazy, singleton.
 *
 * Env (build-time, Vite):
 *   VITE_PADDLE_CLIENT_TOKEN          — client-side token from Paddle
 *   VITE_PADDLE_ENV                   — 'sandbox' (default) or 'production'
 *   VITE_PADDLE_TEACHER_PRICE_MONTHLY — pri_… for £15/mo SSi Premium
 *   VITE_PADDLE_TEACHER_PRICE_ANNUAL  — pri_… for £150/yr SSi Premium (tutor + school annual)
 *   VITE_PADDLE_SCHOOL_TEACHER_PRICE_ANNUAL — pri_… for £150/yr/teacher SCHOOL PLATFORM
 *                                              (per-seat; quantity = seats). OPTIONAL OVERRIDE
 *                                              ONLY — see the monthly entry below; falls
 *                                              through the tutor annual env var and then onto
 *                                              SSI_PREMIUM_ANNUAL_PRICE_ID.
 *   VITE_PADDLE_STUDENT_PRICE_MONTHLY        — pri_… for £10/mo student-via-TUTOR (ACT)
 *   VITE_PADDLE_STUDENT_SCHOOL_PRICE_MONTHLY — pri_… for £5/mo student-via-SCHOOL (no commission)
 *   VITE_PADDLE_STUDENT_SCHOOL_PRICE_ANNUAL  — pri_… for £50/yr student-via-SCHOOL (no commission)
 *   (VITE_PADDLE_STUDENT_PRICE_ANNUAL — £100/yr student-via-TUTOR — is RETIRED.
 *    Founder ruling 2026-08-02: tutor-class students are monthly-only, to cap
 *    rebate exposure. The price still exists in Paddle and in the webhook's
 *    PRICE_CATALOG so any historical annual tutor subscription keeps resolving,
 *    but nothing in the app can start a new one. School students keep annual.)
 *   VITE_PADDLE_SCHOOL_TEACHER_PRICE_MONTHLY — pri_… for £15/teacher/mo SCHOOL PLATFORM
 *                                              (per-seat; checkout sends quantity = teacher seats).
 *                                              Founder ruling 2026-09-07: "Teacher prices are
 *                                              in there. Teachers are just regular users" — no
 *                                              separate school-teacher Paddle product exists or
 *                                              should exist, and NOTHING has to be created by
 *                                              hand. A school is quantity>1 of the existing
 *                                              per-seat SSi Premium price; the webhook tells the
 *                                              lanes apart by customData.kind ('school_platform'
 *                                              vs 'org_platform' vs 'tutor_platform'), never by
 *                                              price id. Same reasoning as the org-seat ruling of
 *                                              2026-08-02 below. Both school env vars are
 *                                              OPTIONAL OVERRIDES: unset, they fall through the
 *                                              tutor env vars and land on the in-repo
 *                                              SSI_PREMIUM_*_PRICE_ID constants, so school
 *                                              checkout resolves with zero Vercel env config.
 *   VITE_PADDLE_EXTRA_CLASS_MONTHLY   — pri_… (optional, future)
 *   VITE_PADDLE_EXTRA_CLASS_ANNUAL    — pri_… (optional, future)
 *   VITE_PADDLE_FAMILY_PRICE_MONTHLY  — pri_… for £25/mo SSi Family (up to 6 accounts)
 *   VITE_PADDLE_FAMILY_PRICE_ANNUAL   — pri_… for £250/yr SSi Family
 *                                        ⚠️ NO FALLBACK — Tom creates this product by
 *                                        hand (FAMILY-PLAN-SPEC.md §2.1); until both
 *                                        env vars are set the Family paywall option
 *                                        stays hidden (graceful, same pattern as the
 *                                        annual-price-unset case above).
 *   VITE_PADDLE_ORG_SEAT_PRICE_MONTHLY — pri_… for £15/seat/mo ORG PLATFORM (workplace/
 *                                        group-leader lane, api/_utils/orgPlatform.ts).
 *                                        Founder ruling 2026-08-02: no separate org-seat
 *                                        Paddle product exists or should exist — the org
 *                                        lane always resolves to the existing SSi Premium
 *                                        product. Falls through schoolTeacherMonthlyPriceId
 *                                        and teacherMonthlyPriceId's env vars first, then
 *                                        lands on the in-repo SSI_PREMIUM_MONTHLY_PRICE_ID
 *                                        constant — so it resolves correctly even with zero
 *                                        Vercel env config. The webhook tells the lanes
 *                                        apart by customData.kind ('org_platform' vs
 *                                        'school_platform'), never by price id.
 *   VITE_PADDLE_ORG_SEAT_PRICE_ANNUAL  — pri_… for £150/seat/yr ORG PLATFORM. Same
 *                                        fallback reasoning — ends on
 *                                        SSI_PREMIUM_ANNUAL_PRICE_ID when unset.
 *   VITE_PADDLE_VOICE_PRICE_MONTHLY   — pri_… for £5/mo SSi Voice (job #490): the ONE
 *                                        per-learner voice entitlement, included in
 *                                        Premium and Family, sold only at the padlock.
 *                                        Optional override; falls back to the LIVE
 *                                        price SSI_VOICE_MONTHLY_PRICE_ID below (created
 *                                        2026-09-21 on Tom's instruction), the same way
 *                                        the SSi Premium ids do. The webhook's catalogue
 *                                        carries the same id (api/_utils/voiceEntitlement.ts).
 *
 * Every value is trimmed at read — Vercel's env-var entry flow can capture
 * trailing newlines, and an untrimmed "pri_…\n" makes Paddle 400 with
 * entity_not_found on checkout.
 */

import { initializePaddle, type CheckoutOpenOptions, type Paddle } from '@paddle/paddle-js'
import { CHECKOUT_COMPLETED_EVENT } from './checkoutEvents'
import { affiliateCodeForCheckout } from '../platform/affiliateTouch'

function trimEnv(v: string | undefined): string | undefined {
  const t = v?.trim()
  return t ? t : undefined
}

// The live SSi Premium (consumer) Paddle prices — the ONE product every
// per-seat/per-tutor lane ultimately falls back to when its own env var is
// unset. Named here so the org lane (below) can fall back to them directly
// in-repo, rather than only through a chain of raw env-var reads that go
// undefined if Vercel env isn't configured (founder ruling 2026-08-02: the
// org lane must use the existing SSi Premium product, never a separate one).
const SSI_PREMIUM_MONTHLY_PRICE_ID = 'pri_01kqq85gvncyasfmfvvpcv1xfg'
const SSI_PREMIUM_ANNUAL_PRICE_ID = 'pri_01kqq86ymc3yhm8be3w7f7kgr1'
// The live SSi Voice price — £5/month, product pro_01m324s2d3vd45vvktytfzbw72.
// Mirror of SSI_VOICE_MONTHLY_PRICE_ID in api/_utils/voiceEntitlement.ts.
const SSI_VOICE_MONTHLY_PRICE_ID = 'pri_01m324tw89apky0semq7jd2rt7'

export const paddleConfig = {
  clientToken: trimEnv(import.meta.env.VITE_PADDLE_CLIENT_TOKEN as string | undefined),
  env: trimEnv(import.meta.env.VITE_PADDLE_ENV as string | undefined) || 'sandbox',
  // Env wins, but fall back to the live SSi Premium price IDs so checkout works
  // without per-environment config (matches the student-price pattern). Prices
  // change rarely; regional variation is Paddle country-specific prices ON THE
  // SAME price ID, so no extra IDs/env vars are needed for regions.
  teacherMonthlyPriceId:
    trimEnv(import.meta.env.VITE_PADDLE_TEACHER_PRICE_MONTHLY as string | undefined) ||
    SSI_PREMIUM_MONTHLY_PRICE_ID, // £15/mo SSi Premium
  teacherAnnualPriceId:
    trimEnv(import.meta.env.VITE_PADDLE_TEACHER_PRICE_ANNUAL as string | undefined) ||
    SSI_PREMIUM_ANNUAL_PRICE_ID, // £150/yr SSi Premium
  studentMonthlyPriceId: trimEnv(import.meta.env.VITE_PADDLE_STUDENT_PRICE_MONTHLY as string | undefined),
  studentSchoolMonthlyPriceId: trimEnv(import.meta.env.VITE_PADDLE_STUDENT_SCHOOL_PRICE_MONTHLY as string | undefined),
  // (studentAnnualPriceId retired 2026-08-02 — tutor students are monthly-only.)
  studentSchoolAnnualPriceId: trimEnv(import.meta.env.VITE_PADDLE_STUDENT_SCHOOL_PRICE_ANNUAL as string | undefined),
  // School platform — £15/teacher/mo, per-seat. There is only ONE £15 Paddle
  // price underneath: a school is just quantity>1 of the same per-unit price as
  // the tutor plan (the webhook tells them apart by customData.kind, NOT by
  // price). So this falls back to the teacher price when its own env var isn't
  // set — no separate Paddle product/price needed. The school checkout sends
  // quantity = teacher seats; the webhook (kind:'school_platform') reads it back.
  schoolTeacherMonthlyPriceId:
    trimEnv(import.meta.env.VITE_PADDLE_SCHOOL_TEACHER_PRICE_MONTHLY as string | undefined) ||
    trimEnv(import.meta.env.VITE_PADDLE_TEACHER_PRICE_MONTHLY as string | undefined) ||
    SSI_PREMIUM_MONTHLY_PRICE_ID,
  // School ANNUAL platform price — same single-product reasoning as the monthly
  // fallback above: there is ONE per-seat annual Paddle price (a school is just
  // quantity>1 of the same per-unit price as the tutor annual plan). Falls back
  // to the tutor annual price when its own env var isn't set, so no separate
  // Paddle product is required.
  // Like the org lane below, both school prices land on the in-repo SSi
  // Premium constants when no env var is configured (founder ruling
  // 2026-08-02: one product, £15/£150 per seat, for every adult lane) — so
  // school checkout can never resolve to an empty price id.
  schoolTeacherAnnualPriceId:
    trimEnv(import.meta.env.VITE_PADDLE_SCHOOL_TEACHER_PRICE_ANNUAL as string | undefined) ||
    trimEnv(import.meta.env.VITE_PADDLE_TEACHER_PRICE_ANNUAL as string | undefined) ||
    SSI_PREMIUM_ANNUAL_PRICE_ID,
  // Org / workplace platform — founder ruling 2026-08-02: no separate org-seat
  // Paddle product exists or should exist. The org lane uses the EXISTING SSi
  // Premium product, same £15/seat/mo, £150/seat/yr as the school teacher
  // seat. Falls through env overrides first, then lands on the in-repo SSi
  // Premium constants (not just a further env-var read) so checkout resolves
  // correctly even when none of the Vercel env vars are configured.
  orgSeatMonthlyPriceId:
    trimEnv(import.meta.env.VITE_PADDLE_ORG_SEAT_PRICE_MONTHLY as string | undefined) ||
    trimEnv(import.meta.env.VITE_PADDLE_SCHOOL_TEACHER_PRICE_MONTHLY as string | undefined) ||
    trimEnv(import.meta.env.VITE_PADDLE_TEACHER_PRICE_MONTHLY as string | undefined) ||
    SSI_PREMIUM_MONTHLY_PRICE_ID,
  orgSeatAnnualPriceId:
    trimEnv(import.meta.env.VITE_PADDLE_ORG_SEAT_PRICE_ANNUAL as string | undefined) ||
    trimEnv(import.meta.env.VITE_PADDLE_SCHOOL_TEACHER_PRICE_ANNUAL as string | undefined) ||
    trimEnv(import.meta.env.VITE_PADDLE_TEACHER_PRICE_ANNUAL as string | undefined) ||
    SSI_PREMIUM_ANNUAL_PRICE_ID,
  extraClassMonthlyPriceId: trimEnv(import.meta.env.VITE_PADDLE_EXTRA_CLASS_MONTHLY as string | undefined),
  extraClassAnnualPriceId: trimEnv(import.meta.env.VITE_PADDLE_EXTRA_CLASS_ANNUAL as string | undefined),
  familyMonthlyPriceId: trimEnv(import.meta.env.VITE_PADDLE_FAMILY_PRICE_MONTHLY as string | undefined),
  familyAnnualPriceId: trimEnv(import.meta.env.VITE_PADDLE_FAMILY_PRICE_ANNUAL as string | undefined),
  voiceMonthlyPriceId:
    trimEnv(import.meta.env.VITE_PADDLE_VOICE_PRICE_MONTHLY as string | undefined) ||
    SSI_VOICE_MONTHLY_PRICE_ID, // £5/mo SSi Voice
} as const

/**
 * WHICH DEPLOYMENT'S WEBHOOK OWNS THIS PURCHASE (job #862). Staging and
 * production both sell through the ONE live Paddle account, and Paddle sends
 * every event to both webhook destinations. So every checkout says where it was
 * opened, in customData.ssi_env; the production webhook skips 'staging' and the
 * staging webhook takes only 'staging' (api/_utils/paddleEnv.ts). Paddle copies
 * a checkout's customData onto the subscription and its renewals, so the mark
 * follows the purchase for life. Only a build of `main` is production.
 */
export const BILLING_ENV: 'production' | 'staging' =
  (typeof __BUILD_BRANCH__ !== 'undefined' ? __BUILD_BRANCH__ : null) === 'main' ? 'production' : 'staging'

/**
 * Wrap Checkout.open once so no call site can forget the mark. The same wrap carries the
 * device's affiliate first touch (job #433, platform/affiliateTouch) as customData
 * .affiliate_code — for audit and reconciliation only: the webhook pays an affiliate from the
 * server's own attribution row, never from customData, which the browser composes.
 */
export function tagCheckoutsWithBillingEnv(instance: Paddle, env: string = BILLING_ENV): void {
  const open = instance.Checkout.open.bind(instance.Checkout)
  instance.Checkout.open = ((settings: CheckoutOpenOptions) => {
    const affiliate = affiliateCodeForCheckout()
    return open({
      ...settings,
      customData: { ...(settings?.customData || {}), ...(affiliate ? { affiliate_code: affiliate } : {}), ssi_env: env },
    })
  }) as typeof open
}

let paddleInstance: Paddle | null = null
let loadingPromise: Promise<Paddle> | null = null

export async function getPaddle(): Promise<Paddle> {
  if (paddleInstance) return paddleInstance
  if (loadingPromise) return loadingPromise

  const token = paddleConfig.clientToken
  if (!token) {
    throw new Error('VITE_PADDLE_CLIENT_TOKEN is not configured')
  }

  const environment = paddleConfig.env === 'production' ? 'production' : 'sandbox'

  loadingPromise = (async () => {
    const instance = await initializePaddle({
      token,
      environment,
      // THE BUYER'S RECEIPT, IN-PAGE. Until this existed the app learnt about a
      // completed purchase in exactly one way: Paddle redirecting the parent
      // window to successUrl (?just_subscribed=1), which useSubscription then
      // polls on. Every path where that redirect does not land — a blocked
      // top-level navigation, a standalone PWA, the buyer closing the overlay
      // on the receipt screen, an Apple Pay sheet that returns to the page it
      // started on — left the app believing the person had no subscription,
      // with their money already taken. Tom, 2026-09-07: a fresh account bought
      // SSi Family for £25, the webhook wrote the row correctly one second
      // later, and Settings still offered him the Upgrade row.
      //
      // Paddle tells us directly. Re-broadcast it as a DOM event so this module
      // stays a pure loader with no app imports; useSubscription listens and
      // polls until the webhook's row appears.
      //
      // IT CARRIES THE TRANSACTION ID (job #361). Two listeners want this one
      // event: useSubscription drops its cached pre-purchase answer and
      // converges silently, and useCheckout puts the buyer in a waiting state
      // that says their payment is confirmed. The second one shows the payment
      // reference on screen, so a person whose plan is slow to appear has the
      // one fact that makes a support message answerable — and it is only ever
      // available here, in Paddle's own event.
      eventCallback: (event: { name?: string; data?: { transaction_id?: unknown; id?: unknown } }) => {
        if (event?.name !== 'checkout.completed') return
        try {
          const raw = event.data?.transaction_id ?? event.data?.id
          window.dispatchEvent(
            new CustomEvent(CHECKOUT_COMPLETED_EVENT, {
              detail: { transactionId: typeof raw === 'string' ? raw : null },
            }),
          )
        } catch {
          // A browser without CustomEvent still gets the successUrl redirect.
        }
      },
    })
    if (!instance) throw new Error('Failed to initialize Paddle.js')
    tagCheckoutsWithBillingEnv(instance)
    paddleInstance = instance
    return instance
  })()

  try {
    return await loadingPromise
  } catch (err) {
    // Don't cache a rejected promise: a transient init failure would otherwise
    // be handed to every future getPaddle() caller, permanently breaking
    // checkout for the whole session. Reset so the next call can retry.
    loadingPromise = null
    throw err
  }
}
